<template>
  <portlet
    :variant="bgColor"
    class="mt-5"
  >
    <form @submit.prevent="onSubmit">
      <form-row>
        <div class="col">
          <h3 class="form_section_title">
            註冊資料
          </h3>
        </div>
      </form-row>
      <form-row>
        <div class="col">
          <div class="enterprise_logo_wrapper">
            <upload-image
              :url="logo"
              name="upload-logo"
              placeholder="LOGO"
              button-text="上傳 LOGO"
              @change="setEnterpriseLogo"
            />
          </div>
        </div>
      </form-row>
      <form-row>
        <form-group
          class="col-md-6 col-sm-12"
          label="統一編號"
          is-required
        >
          <input-group>
            <form-input
              v-model="enterprise.enterprise_gui_number"
              :readonly="lockGuiNumber"
              name="GUI-number"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-6 col-sm-12"
          label="公司名稱"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_name.$error"
            error-message="必填"
          >
            <form-input
              v-model="enterprise.enterprise_name"
              :has-error="$v.enterprise.enterprise_name.$error"
              name="enterprise_name"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-3 col-sm-12"
          label="負責人"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_owner.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_owner.required,
              '限定中英文字': !$v.enterprise.enterprise_owner.englishOrChineseCharactersOnly,
              [`最多 ${ wordLimit.enterprise_owner } 個字`]: !$v.enterprise.enterprise_owner.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_owner"
              :has-error="$v.enterprise.enterprise_owner.$error"
              name="enterprise_owner"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-3 col-sm-12"
          label="成立時間"
          name="founded date"
          is-required
        >
          <date-time-picker
            v-model="enterprise.enterprise_founded_date"
            input-name="founded-date"
            :max-datetime="$moment().toISOString(true)"
            format="yyyy/LL/dd"
          >
            <span
              slot="before"
              aria-hidden="true"
              class="input-group-text text-muted btn-pill"
            >
              <font-awesome-icon icon="calendar-alt" />
            </span>
          </date-time-picker>
        </form-group>
        <form-group
          class="col-md-3 col-sm-12"
          label="員工人數"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_employee_number.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_employee_number.required,
              '必須為整數': !$v.enterprise.enterprise_employee_number.integer,
              [`最多 ${ wordLimit.enterprise_employee_number } 位數`]: !$v.enterprise.enterprise_employee_number.maxLength
            }"
          >
            <template slot="append">
              <span
                aria-hidden="true"
                class="input-group-text text-muted btn-pill"
              >
                人
              </span>
            </template>
            <form-input
              v-model="enterprise.enterprise_employee_number"
              :has-error="$v.enterprise.enterprise_employee_number.$error"
              type="number"
              min="0"
              name="number-of-employees"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-3 col-sm-12"
          label="資本額"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_registered_capital.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_registered_capital.required,
              '必須為數字': !$v.enterprise.enterprise_registered_capital.integer,
              [`最多 ${ wordLimit.enterprise_register_capital } 位數`]: !$v.enterprise.enterprise_registered_capital.maxLength
            }"
          >
            <template slot="append">
              <span
                aria-hidden="true"
                class="input-group-text text-muted btn-pill"
              >
                萬
              </span>
            </template>
            <form-input
              v-model="enterprise.enterprise_registered_capital"
              :has-error="$v.enterprise.enterprise_registered_capital.$error"
              type="number"
              min="0"
              name="registered-capital"
              placeholder="100"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-4 col-sm-12"
          label="產業類別"
          is-required
        >
          <input-group>
            <form-select
              :default-value="enterprise.enterprise_industry"
              :options="categoriesGetter"
              select-name="category_name"
              select-value="category_value"
              name="industries"
              @change="industrySelected"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-8 col-sm-12"
          :label="`營業項目(最多 ${ wordLimit.enterprise_services } 個字) ${ $options.filters.countWords(enterprise.enterprise_services, wordLimit.enterprise_services)}`"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_services.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_services.required,
              [`最多 ${ wordLimit.enterprise_services } 個字`]: !$v.enterprise.enterprise_services.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_services"
              :has-error="$v.enterprise.enterprise_services.$error"
              :max-length="126"
              name="services"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-4 col-sm-12"
          label="聯絡人"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_hr_contact_person.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_hr_contact_person.required,
              '限定中英文字': !$v.enterprise.enterprise_hr_contact_person.englishOrChineseCharactersOnly,
              [`最多 ${ wordLimit.enterprise_hr_contact_person } 個字`]: !$v.enterprise.enterprise_hr_contact_person.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_hr_contact_person"
              :has-error="$v.enterprise.enterprise_hr_contact_person.$error"
              name="contact-person"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-4 col-sm-12"
          label="聯絡人 e-mail"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_hr_contact_email.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_hr_contact_email.required,
              '信箱無效': !$v.enterprise.enterprise_hr_contact_email.email,
              [`最多 ${ wordLimit.enterprise_hr_contact_email } 個字`]: !$v.enterprise.enterprise_hr_contact_email.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_hr_contact_email"
              :has-error="$v.enterprise.enterprise_hr_contact_email.$error"
              name="contact-email"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-4 col-sm-12"
          label="聯絡人電話"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_hr_contact_tel.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_hr_contact_tel.required,
              '電話格式無效': !$v.enterprise.enterprise_hr_contact_tel.phoneIncludeSomeCharactersOnly,
              [`最多 ${ wordLimit.enterprise_hr_contact_tel } 位數`]: !$v.enterprise.enterprise_hr_contact_tel.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_hr_contact_tel"
              :has-error="$v.enterprise.enterprise_hr_contact_tel.$error"
              name="contact-tel"
              placeholder="02-12345678_#123"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-4 col-sm-12"
          label="公司網址"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_website.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_website.required,
              '無效的網址': !$v.enterprise.enterprise_website.url,
              [`最多 ${ wordLimit.enterprise_website } 個字`]: !$v.enterprise.enterprise_website.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_website"
              :has-error="$v.enterprise.enterprise_website.$error"
              name="enterprise-website"
              placeholder="https://www.example.com"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-8 col-sm-12"
          label="公司地址"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_address.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_address.required,
              [`最多 ${ wordLimit.enterprise_address } 個字`]: !$v.enterprise.enterprise_address.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_address"
              :has-error="$v.enterprise.enterprise_address.$error"
              name="enterprise-address"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-4 col-sm-12"
          label="公司電話"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_tel.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_tel.required,
              '電話格式無效': !$v.enterprise.enterprise_tel.phoneIncludeSomeCharactersOnly,
              [`最多 ${ wordLimit.enterprise_tel } 位數`]: !$v.enterprise.enterprise_tel.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_tel"
              :has-error="$v.enterprise.enterprise_tel.$error"
              name="tel"
              placeholder="02-12345678_#123"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-4 col-sm-12"
          label="傳真"
          placeholder="02-12345678"
        >
          <input-group
            :has-error="$v.enterprise.enterprise_fax.$error"
            :error-message="{
              '電話格式無效': !$v.enterprise.enterprise_fax.phoneIncludeSomeCharactersOnly,
              [`最多 ${ wordLimit.enterprise_fax } 位數`]: !$v.enterprise.enterprise_fax.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_fax"
              :has-error="$v.enterprise.enterprise_fax.$error"
              name="fax"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-md-4 col-sm-12"
          label="關係企業"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_related_company.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_related_company.required,
              [`最多 ${ wordLimit.enterprise_related_company } 個字`]: !$v.enterprise.enterprise_related_company.maxLength
            }"
          >
            <form-input
              v-model="enterprise.enterprise_related_company"
              :has-error="$v.enterprise.enterprise_related_company.$error"
              name="related-company"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-2 col-sm-12 col-6"
          label="工作時段"
          is-required
        >
          <template slot="form-check">
            <check-input
              v-model="enterprise.enterprise_is_nine_to_five"
              label="日班"
              type="radio"
              name="enterprise-is-nine-to-five"
              :no-pl="true"
              @change="updateIsNineToFive"
            />
          </template>
        </form-group>
        <form-group
          class="col-md-2 col-sm-12 col-6"
          label=" "
        >
          <template slot="form-check">
            <check-input
              v-model="enterprise.enterprise_is_shift_work"
              label="輪班"
              type="radio"
              name="enterprise-is-shift"
              :no-pl="true"
              @change="updateIsShift"
            />
          </template>
        </form-group>
        <template v-if="enterprise.enterprise_is_nine_to_five">
          <form-group
            class="col-md-3 col-sm-12"
            label=" "
          >
            <time-picker
              v-model="enterprise.enterprise_shift_schedule[0]"
              input-name="start-time"
            />
          </form-group>
          <form-group
            class="col-sm-4 col-12"
            label=" "
          >
            <template slot="form-check">
              <div class="form-check">
                <check-input
                  v-model="enterprise.enterprise_is_flextime"
                  label="彈性上下班"
                  type="checkbox"
                  name="enterprise-is-flextime"
                  :no-pl="true"
                />
              </div>
            </template>
          </form-group>
        </template>
        <template v-else>
          <form-group
            class="col-md-3 col-sm-12"
            label=" "
          >
            <button-wrapper
              @click.prevent="addShift"
            >
              新增時段 <font-awesome-icon :icon="['far', 'plus-square']" />
            </button-wrapper>
          </form-group>
        </template>
      </form-row>
      <form-row v-if="enterprise.enterprise_is_shift_work">
        <form-group
          v-for="(shift, index) in enterprise.enterprise_shift_schedule"
          :key="index"
          cols="4"
          label=" "
        >
          <time-picker
            v-model="enterprise.enterprise_shift_schedule[index]"
            input-name="start-time"
          />
          <span
            v-show="index !== 0"
            class="duration_close"
            @click.prevent="removeShift(index, $event)"
          >
            <font-awesome-icon icon="times-circle" />
          </span>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-sm-6 col-12"
          :label="`公司介紹(最多 ${ wordLimit.enterprise_introduction } 個字) ${ $options.filters.countWords(enterprise.enterprise_introduction, wordLimit.enterprise_introduction)}`"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_introduction.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_introduction.required,
              [`最多 ${ wordLimit.enterprise_introduction } 個字`]: !$v.enterprise.enterprise_introduction.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_introduction"
              :has-error="$v.enterprise.enterprise_introduction.$error"
              name="introduction"
              :max-length="228"
              :row="4"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-6 col-12"
          :label="`教育訓練(最多 ${wordLimit.enterprise_employee_training_introduction} 個字) ${ $options.filters.countWords(enterprise.enterprise_employee_training_introduction, wordLimit.enterprise_employee_training_introduction)}`"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_employee_training_introduction.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_employee_training_introduction.required,
              [`最多 ${ wordLimit.enterprise_employee_training_introduction } 個字`]: !$v.enterprise.enterprise_employee_training_introduction.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_employee_training_introduction"
              :has-error="$v.enterprise.enterprise_employee_training_introduction.$error"
              name="employee-training-introduction"
              :max-length="228"
              :row="4"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-sm-6 col-12"
          :label="`福利制度(最多 ${ wordLimit.enterprise_employee_benefits } 個字) ${ $options.filters.countWords(enterprise.enterprise_employee_benefits, wordLimit.enterprise_employee_benefits)}`"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_employee_benefits.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_employee_benefits.required,
              [`最多 ${ wordLimit.enterprise_employee_benefits } 個字`]: !$v.enterprise.enterprise_employee_benefits.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_employee_benefits"
              :has-error="$v.enterprise.enterprise_employee_benefits.$error"
              name="employee-benefits"
              :max-length="228"
              :row="4"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-6 col-12"
          :label="`徵才內容(最多 ${ wordLimit.enterprise_recruiting_description } 個字) ${ $options.filters.countWords(enterprise.enterprise_recruiting_description, wordLimit.enterprise_recruiting_description)}`"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_recruiting_description.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_recruiting_description.required,
              [`最多 ${ wordLimit.enterprise_recruiting_description } 個字`]: !$v.enterprise.enterprise_recruiting_description.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_recruiting_description"
              :has-error="$v.enterprise.enterprise_recruiting_description.$error"
              name="recruiting-description"
              :max-length="228"
              :row="4"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-sm-6 col-12"
          :label="`求職準備資料(最多 ${ wordLimit.enterprise_work_portfolio } 個字) ${ $options.filters.countWords(enterprise.enterprise_work_portfolio, wordLimit.enterprise_work_portfolio)}`"
          is-required
        >
          <input-group
            :has-error="$v.enterprise.enterprise_work_portfolio.$error"
            :error-message="{
              '必填': !$v.enterprise.enterprise_work_portfolio.required,
              [`最多 ${ wordLimit.enterprise_work_portfolio } 個字`]: !$v.enterprise.enterprise_work_portfolio.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_work_portfolio"
              :has-error="$v.enterprise.enterprise_work_portfolio.$error"
              name="work-portfolio"
              :max-length="50"
              :row="4"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-6 col-12"
          :label="`備註(最多 ${ wordLimit.enterprise_remark } 個字) ${ $options.filters.countWords(enterprise.enterprise_remark, wordLimit.enterprise_remark)}`"
        >
          <input-group
            :has-error="$v.enterprise.enterprise_remark.$error"
            :error-message="{
              [`最多 ${ wordLimit.enterprise_remark } 個字`]: !$v.enterprise.enterprise_remark.maxLength
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_remark"
              :has-error="$v.enterprise.enterprise_remark.$error"
              name="remark"
              :max-length="50"
              :row="4"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-md-4 col-sm-4"
        >
          <template slot="form-check">
            <div class="form-check">
              <check-input
                v-model="enterprise.enterprise_hire_interns"
                label="提供實習機會"
                type="switch"
                name="does-hire-interns"
              />
            </div>
          </template>
        </form-group>
        <form-group
          class="col-md-4 col-sm-4"
        >
          <template slot="form-check">
            <div class="form-check">
              <check-input
                v-model="enterprise.enterprise_hire_foreigners"
                label="提供外籍人士職缺"
                type="switch"
                name="does-hire-foreigners"
              />
            </div>
          </template>
        </form-group>

        <form-group
          class="col-md-4 col-sm-4"
        >
          <template slot="form-check">
            <div class="form-check">
              <check-input
                v-model="enterprise.enterprise_hire_disabled_people"
                label="提供身心障礙職缺"
                type="switch"
                name="does-hire-disabled-people"
              />
            </div>
          </template>
        </form-group>
      </form-row>
      <form-section-divider />
      <form-row>
        <form-group
          class="col-sm-4 col-12"
          label="Corporation"
          is-required
        >
          <input-group
            :has-error="vKeys.includes('enterprise_name_en')
              ? $v.enterprise.enterprise_name_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_name_en') &&
                Object.keys($v.enterprise.enterprise_name_en).includes('required')
                ? !$v.enterprise.enterprise_name_en.required
                : false,
              '不允許中文字符': vKeys.includes('enterprise_name_en')
                ? !$v.enterprise.enterprise_name_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-input
              v-model="enterprise.enterprise_name_en"
              :has-error="vKeys.includes('enterprise_name_en')
                ? $v.enterprise.enterprise_name_en.$error
                : false"
              name="enterprise_name_en"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-4 col-12"
          label="Related Corporation"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_related_company_en')
              ? $v.enterprise.enterprise_related_company_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_related_company_en') &&
                Object.keys($v.enterprise.enterprise_related_company_en).includes('required')
                ? !$v.enterprise.enterprise_related_company_en.required
                : false,
              '不允許中文字符': vKeys.includes('enterprise_related_company_en')
                ? !$v.enterprise.enterprise_related_company_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-input
              v-model="enterprise.enterprise_related_company_en"
              :has-error="vKeys.includes('enterprise_related_company_en')
                ? $v.enterprise.enterprise_related_company_en.$error
                : false"
              name="enterprise_related_company_en"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-4 col-12"
          label="Contact"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_hr_contact_person_en')
              ? $v.enterprise.enterprise_hr_contact_person_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_hr_contact_person_en') &&
                Object.keys($v.enterprise.enterprise_related_company_en).includes('required')
                ? !$v.enterprise.enterprise_hr_contact_person_en.required
                : false,
              '不允許中文字符': vKeys.includes('enterprise_hr_contact_person_en')
                ? !$v.enterprise.enterprise_hr_contact_person_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-input
              v-model="enterprise.enterprise_hr_contact_person_en"
              :has-error="vKeys.includes('enterprise_hr_contact_person_en')
                ? $v.enterprise.enterprise_hr_contact_person_en.$error
                : false"
              name="contact-person-en"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          cols="12"
          label="Address"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_address_en')
              ? $v.enterprise.enterprise_address_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_address_en') &&
                Object.keys($v.enterprise.enterprise_address_en).includes('required')
                ? !$v.enterprise.enterprise_address_en.required
                : false,
              '不允許中文字符': vKeys.includes('enterprise_address_en')
                ? !$v.enterprise.enterprise_address_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-input
              v-model="enterprise.enterprise_address_en"
              :has-error="vKeys.includes('enterprise_address_en')
                ? $v.enterprise.enterprise_address_en.$error
                : false"
              name="address-en"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-sm-6 col-12"
          :label="`Running Project ${ $options.filters.countWords(enterprise.enterprise_services_en, wordLimit.enterprise_services_en)}`"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_services_en')
              ? $v.enterprise.enterprise_services_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_services_en') &&
                Object.keys($v.enterprise.enterprise_services_en).includes('required')
                ? !$v.enterprise.enterprise_services_en.required
                : false,
              [`最多 ${wordLimit.enterprise_services_en} 個字`]: vKeys.includes('enterprise_services_en')
                ? !$v.enterprise.enterprise_services_en.maxLength
                : false,
              '不允許中文字符': vKeys.includes('enterprise_services_en')
                ? !$v.enterprise.enterprise_services_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_services_en"
              :has-error="vKeys.includes('enterprise_services_en')
                ? $v.enterprise.enterprise_services_en.$error
                : false"
              :max-length="374"
              name="services-en"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-6 col-12"
          :label="`Salary & Welfare ${ $options.filters.countWords(enterprise.enterprise_employee_benefits_en, wordLimit.enterprise_employee_benefits_en)}`"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_employee_benefits_en')
              ? $v.enterprise.enterprise_employee_benefits_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_employee_benefits_en') &&
                Object.keys($v.enterprise.enterprise_employee_benefits_en).includes('required')
                ? !$v.enterprise.enterprise_employee_benefits_en.required
                : false,
              [`最多 ${wordLimit.enterprise_employee_benefits_en} 個字`]: vKeys.includes('enterprise_employee_benefits_en')
                ? !$v.enterprise.enterprise_employee_benefits_en.maxLength
                : false,
              '不允許中文字符': vKeys.includes('enterprise_employee_benefits_en')
                ? !$v.enterprise.enterprise_employee_benefits_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_employee_benefits_en"
              :has-error="vKeys.includes('enterprise_employee_benefits_en')
                ? $v.enterprise.enterprise_employee_benefits_en.$error
                : false"
              :max-length="576"
              name="employee-benefits-en"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-sm-6 col-12"
          :label="`Orientation ${ $options.filters.countWords(enterprise.enterprise_employee_training_introduction_en, wordLimit.enterprise_employee_training_introduction_en)}`"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_employee_training_introduction_en')
              ? $v.enterprise.enterprise_employee_training_introduction_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_employee_training_introduction_en') &&
                Object.keys($v.enterprise.enterprise_employee_training_introduction_en).includes('required')
                ? !$v.enterprise.enterprise_employee_training_introduction_en.required
                : false,
              [`最多 ${wordLimit.enterprise_employee_training_introduction_en} 個字`]: vKeys.includes('enterprise_employee_training_introduction_en')
                ? !$v.enterprise.enterprise_employee_training_introduction_en.maxLength
                : false,
              '不允許中文字符': vKeys.includes('enterprise_employee_training_introduction_en')
                ? !$v.enterprise.enterprise_employee_training_introduction_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_employee_training_introduction_en"
              :has-error="vKeys.includes('enterprise_employee_training_introduction_en')
                ? $v.enterprise.enterprise_employee_training_introduction_en.$error
                : false"
              :max-length="576"
              name="employee-training-introduction-en"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-6 col-12"
          :label="`Requirement ${ $options.filters.countWords(enterprise.enterprise_recruiting_description_en, wordLimit.enterprise_recruiting_description_en)}`"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_recruiting_description_en')
              ? $v.enterprise.enterprise_recruiting_description_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_recruiting_description_en') &&
                Object.keys($v.enterprise.enterprise_recruiting_description_en).includes('required')
                ? !$v.enterprise.enterprise_recruiting_description_en.required
                : false,
              [`最多 ${wordLimit.enterprise_recruiting_description_en} 個字`]: vKeys.includes('enterprise_recruiting_description_en')
                ? !$v.enterprise.enterprise_recruiting_description_en.maxLength
                : false,
              '不允許中文字符': vKeys.includes('enterprise_recruiting_description_en')
                ? !$v.enterprise.enterprise_recruiting_description_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_recruiting_description_en"
              :has-error="vKeys.includes('enterprise_recruiting_description_en')
                ? $v.enterprise.enterprise_recruiting_description_en.$error
                : false"
              :max-length="576"
              name="recruiting-description-en"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-row>
        <form-group
          class="col-sm-6 col-12"
          :label="`Note ${ $options.filters.countWords(enterprise.enterprise_note_en, wordLimit.enterprise_note_en)}`"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_note_en')
              ? $v.enterprise.enterprise_note_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_note_en') &&
                Object.keys($v.enterprise.enterprise_note_en).includes('required')
                ? !$v.enterprise.enterprise_note_en.required
                : false,
              [`最多 ${ wordLimit.enterprise_note_en } 個字`]: vKeys.includes('enterprise_note_en')
                ? !$v.enterprise.enterprise_note_en.maxLength
                : false,
              '不允許中文字符': vKeys.includes('enterprise_note_en')
                ? !$v.enterprise.enterprise_note_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_note_en"
              :has-error="vKeys.includes('enterprise_note_en')
                ? $v.enterprise.enterprise_note_en.$error
                : false"
              :max-length="100"
              name="note-en"
            />
          </input-group>
        </form-group>
        <form-group
          class="col-sm-6 col-12"
          :label="`Necessary documents ${ $options.filters.countWords(enterprise.enterprise_work_portfolio_en, wordLimit.enterprise_work_portfolio_en)}`"
          :is-required="enterprise.enterprise_hire_foreigners"
        >
          <input-group
            :has-error="vKeys.includes('enterprise_work_portfolio_en')
              ? $v.enterprise.enterprise_work_portfolio_en.$error
              : false"
            :error-message="{
              '必填': vKeys.includes('enterprise_work_portfolio_en') &&
                Object.keys($v.enterprise.enterprise_work_portfolio_en).includes('required')
                ? !$v.enterprise.enterprise_work_portfolio_en.required
                : false,
              [`最多 ${ wordLimit.enterprise_work_portfolio_en } 個字`]: vKeys.includes('enterprise_work_portfolio_en')
                ? !$v.enterprise.enterprise_work_portfolio_en.maxLength
                : false,
              '不允許中文字符': vKeys.includes('enterprise_work_portfolio_en')
                ? !$v.enterprise.enterprise_work_portfolio_en.englishOrCharactersOnly
                : false
            }"
          >
            <form-textarea
              v-model="enterprise.enterprise_work_portfolio_en"
              :has-error="vKeys.includes('enterprise_work_portfolio_en')
                ? $v.enterprise.enterprise_work_portfolio_en.$error
                : false"
              :max-length="100"
              name="remark-en"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-section-divider />
      <form-row>
        <div class="col">
          <h3 class="form_section_title">
            密碼設定<span class="ps_text">* 密碼最少為6碼</span>
          </h3>
        </div>
      </form-row>
      <form-row>
        <form-group
          cols="6"
          label="密碼"
        >
          <input-group
            :has-error="$v.enterprise.password.$error"
            :error-message="{'最少六個英數字': !$v.enterprise.password.minLength}"
          >
            <form-input
              v-model="enterprise.password"
              :has-error="$v.enterprise.password.$error"
              type="password"
              name="enterprise_password"
              autocomplete="no"
            />
          </input-group>
        </form-group>
        <form-group
          cols="6"
          label="密碼確認"
        >
          <input-group
            :has-error="$v.enterprise.password_confirmation.$error"
            :error-message="{'密碼需一致': !$v.enterprise.password_confirmation.sameAsPassword}"
          >
            <form-input
              v-model="enterprise.password_confirmation"
              :has-error="$v.enterprise.password_confirmation.$error"
              type="password"
              name="enterprise_password_confirmation"
            />
          </input-group>
        </form-group>
      </form-row>
      <form-section-divider v-show="showPrivacyPolicy" />
      <form-row>
        <div class="col">
          <div
            v-if="showPrivacyPolicy"
            class="form-check"
          >
            <check-input
              label="同意"
              name="agree-policy"
              @change="isAgreePrivacyPolicy"
            >
              <template
                slot="label"
              >
                <button-wrapper
                  thickness="xs"
                  @click.prevent="openPolicyWindow"
                >
                  隱私權政策的適用範圍及個人資料安全之保護聲明」
                </button-wrapper>
              </template>
            </check-input>
          </div>
        </div>
        <div class="col-sm-2 col-5 align-self-end">
          <button-wrapper
            fill
            :disabled="cantSubmit"
            @click.prevent="submitData"
          >
            確定
          </button-wrapper>
        </div>
      </form-row>
    </form>
  </portlet>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import { validationMixin } from 'vuelidate'
import Portlet from '~components/common/Portlet'
import FormRow from '~components/common/form/Row'
import FormGroup from '~components/common/form/Group'
import FormInput from '~components/common/form/input'
import InputGroup from '~components/common/form/input/Group'
import ButtonWrapper from '~components/common/buttons'
import FormSelect from '~components/common/form/Select'
import FormSectionDivider from '~components/common/form/section/Divider'
import CheckInput from '~components/common/form/input/Check'
import FormTextarea from '~components/common/form/input/Textarea'
import DateTimePicker from '~lar/DateTimePicker'
import TimePicker from '~components/third-party/larvata/TimePicker'
import UploadImage from '~components/common/form/input/UploadImage'
import enterpriseValidations, { wordLimit } from './enterprise-validations'

export default {
  name: 'EnterpriseProfileForm',
  filters: {
    countWords (value, limit) {
      return value ? `${value.length}/${limit || 100}` : ''
    }
  },
  components: {
    Portlet,
    FormRow,
    FormGroup,
    FormInput,
    InputGroup,
    ButtonWrapper,
    FormSelect,
    FormSectionDivider,
    CheckInput,
    FormTextarea,
    DateTimePicker,
    TimePicker,
    UploadImage
  },
  mixins: [validationMixin],
  props: {
    bgColor: {
      type: String,
      default: ''
    },
    showPrivacyPolicy: {
      type: Boolean,
      default: false
    },
    lockGuiNumber: {
      type: Boolean,
      default: false
    },
    registeredEmail: {
      type: String,
      default: ''
    },
    currentEnterprise: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      cantSubmit: this.showPrivacyPolicy,
      date: '',
      enterprise: {
        enterprise_registered_email: this.registeredEmail,
        enterprise_logo: [],
        enterprise_is_nine_to_five: true,
        enterprise_shift_schedule: [{
          start_time: this.$moment().format('YYYY-MM-DD HH:mm'),
          end_time: this.$moment().format('YYYY-MM-DD HH:mm')
        }],
        enterprise_is_shift_work: false,
        enterprise_is_flextime: false,
        enterprise_hire_interns: false,
        enterprise_hire_foreigners: false,
        enterprise_hire_disabled_people: false
      },
      logo: null,
      wordLimit
    }
  },
  computed: {
    ...mapGetters('pages/jobs', [
      'categoriesGetter'
    ]),
    vKeys () {
      return Object.keys(this.$v.enterprise)
    }
  },
  watch: {
    currentEnterprise (value) {
      this.enterprise = Object.assign({}, this.enterprise, this.currentEnterprise)
      if (this.enterprise.enterprise_logo) {
        this.logo = this.enterprise.enterprise_logo.attachment_url
      }
    }
  },
  validations () {
    return enterpriseValidations(this.enterprise.enterprise_hire_foreigners)
  },
  async created () {
    await this.getCategories()
    this.enterprise = Object.assign({}, this.enterprise, this.currentEnterprise)
  },
  methods: {
    ...mapActions('pages/jobs', [
      'getCategories'
    ]),
    ...mapActions('layouts', [
      'newToast'
    ]),
    openPolicyWindow () {
      const target = this.$router.resolve('/privacy-policy')
      window.open(target.href, '_blank')
    },
    isAgreePrivacyPolicy (isChecked) {
      this.cantSubmit = !isChecked
    },
    onSubmit () {},
    submitData () {
      if (this.checkEnterpriseValidations()) {
        this.$emit('submit', this.enterprise)
        this.$v.enterprise.$reset()
      } else {
        this.newToast({
          variant: 'warning',
          message: '請確認所有欄位是否都有填寫完整'
        })
      }
    },
    checkEnterpriseValidations () {
      this.$v.enterprise.$touch()
      return !this.$v.$invalid
    },
    industrySelected (industry) {
      this.enterprise.enterprise_industry = industry.category_value
    },
    foundedDateSelected ({ startDate }) {
      this.enterprise.enterprise_founded_date = startDate
    },
    setEnterpriseLogo (logo) {
      this.enterprise.enterprise_new_logo = logo
    },
    updateIsShift (isChecked) {
      if (isChecked) {
        this.enterprise.enterprise_is_nine_to_five = this.enterprise.enterprise_is_flextime = false
      }
    },
    updateIsNineToFive (isChecked) {
      if (isChecked) {
        this.enterprise.enterprise_is_shift_work = false
      }
    },
    addShift () {
      this.enterprise.enterprise_shift_schedule.push({
        start_time: this.$moment().format('YYYY-MM-DD HH:mm'),
        end_time: this.$moment().format('YYYY-MM-DD HH:mm')
      })
    },
    removeShift (index) {
      this.enterprise.enterprise_shift_schedule.splice(index, 1)
    }
  }
}
</script>
<style lang="scss" scoped>
.enterprise_logo_wrapper {
  width: 150px;
  height: 150px;
  position: absolute;
  top: -220px;
  left: 0;
  right: 0;
  margin: 0 auto;
  z-index: 10;
}
.duration_close {
  position: absolute;
  width: 15.5px;
  height: 15.5px;
  display: block;
  right: -2px;
  bottom: 35px;
  z-index: 10;
  color: $danger;
}
.portlet_wrapper {
  margin-top: 130px !important;
}
</style>
